/**
 * Author: Gaurav Kumar
 *
 * Module: api.js
 * This module provides utilities for authenticated Http Requests using MSAL and Tanstack React Query
 *
 * Features:
 * - React Query Integration: Provides hooks (`useAuthenticatedQuery`, `useAuthenticatedQueries`) to manage authenticated API calls.
 * - Session Token Management: Handles session storage for tokens (`AUTH_TOKEN_KEY`, `AUTOMATION_TOKEN`).
 *
 * Hooks:
 * - useIsAuthorized: Runs an effect to check if the user is authorized by retrieving a token from session storage.
 * - useAuthenticatedQuery: A wrapper around `useQuery` for making API calls with authentication, managing dependencies and error handling.
 * - useAuthenticatedQueries: A wrapper around `useQueries` to handle multiple API calls requiring authentication.
 *
 * Utilities:
 * - getAuthToken: Retrieves an authentication token from session storage or calls MSAL to acquire a new token.
 * - retrieveNewAuthToken: Uses MSAL to acquire a new token silently, storing it in session storage.
 * - httpCall: Makes an authenticated API call using Axios, handling msal token.
 */

import type { IMsalContext } from '@azure/msal-react';
import { useMsal } from '@azure/msal-react';
import type { NotifyOnChangeProps, UseMutationOptions, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import axios, { isAxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import getSelectedAccount from 'watchtower-ui/api/msalAccount';
import { apiScopes } from 'watchtower-ui/authConfig';
import { useCustomNavigate } from 'watchtower-ui/components/common/CustomNavigation';
import { FEATURE_FLAG_QUERY_PARAM, useFeatureFlag } from 'watchtower-ui/contexts/FeatureFlagContext';
import { AUTH_TOKEN_KEY } from 'watchtower-ui/utils/Constants';

/** @internal */
export const AUTOMATION_TOKEN = 'Automation_token';

export type Response<T> = { success: true; data: T } | { success: false; err?: AxiosResponse; message?: string };

type NavigateFunction = ReturnType<typeof useCustomNavigate>;

export type AuthResponse = { token_type: string; access_token: string };

const ABWP_API_URL = (() => {
  const abwpApiUrl = String(import.meta.env.VITE_ABWP_API_URL ?? '');
  if (abwpApiUrl) {
    console.log('Using Env Vite ABWP API URL:', abwpApiUrl);
    return abwpApiUrl;
  } else {
    const env = String(
      import.meta.env.VITE_ABWP_ENV ?? import.meta.env.VITE_ENVIRONMENT_NAME ?? import.meta.env.MODE ?? 'development',
    ).toLowerCase();

    const urlMap = {
      develop: 'https://api-dev.abwp.beertech.com',
      qa: 'https://api-uat.abwp.beertech.com',
      state: 'https://api-uat.abwp.beertech.com',
      live: 'https://beerforce-app-production.azurewebsites.net',
    } as const;
    console.log('Using custom environment-based ABWP API URL:', urlMap[env as keyof typeof urlMap]);
    return urlMap[env as keyof typeof urlMap] ?? 'https://api-dev.abwp.beertech.com';
  }
})();

interface AbwpUserData {
  id?: string;
  email?: string;
  family_name?: string;
  sub?: string;
  given_name?: string;
  role?: string;
  brands?: string[];
  regions?: string[];
  investmentTypes?: string[];
  nonInvestmentSignals?: boolean;
}

export const hasAccess = (userData: unknown): boolean => {
  if (typeof userData !== 'object' || userData === null) return false;

  const u = userData as Partial<AbwpUserData>;
  return (
    (u.role === 'Default' || u.role === 'Agency') &&
    'brands' in u &&
    'regions' in u &&
    'investmentTypes' in u &&
    'nonInvestmentSignals' in u
  );
};

const retrieveNewAuthToken = async (
  msalDetails: IMsalContext,
  navigate: NavigateFunction,
  enableDeveloperLogs: boolean,
) => {
  const account = getSelectedAccount(msalDetails);
  if (!account) {
    console.warn('Trying to retrieve a token with no active accounts.');
    return null;
  }
  try {
    const response = await msalDetails.instance.acquireTokenSilent({
      scopes: [...apiScopes.openId, ...apiScopes.watchtowerApi],
      account,
      forceRefresh: true,
    });
    if (response.accessToken) {
      sessionStorage.setItem(AUTH_TOKEN_KEY, response.accessToken);
      return response.accessToken;
    }
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 404) {
      navigate({ to: '/unauthorized', keptQueryParams: [FEATURE_FLAG_QUERY_PARAM] });
    } else if (import.meta.env.DEV || enableDeveloperLogs) {
      console.warn(err);
    }
  }
  return null;
};

const getAuthToken = async (msalDetails: IMsalContext, navigate: NavigateFunction, enableDeveloperLogs: boolean) => {
  const token =
    sessionStorage.getItem(AUTOMATION_TOKEN) ??
    sessionStorage.getItem(AUTH_TOKEN_KEY) ??
    (await retrieveNewAuthToken(msalDetails, navigate, enableDeveloperLogs));
  if (token) {
    return `Bearer ${token}`;
  } else {
    sessionStorage.removeItem(AUTH_TOKEN_KEY);
    return null;
  }
};

interface AuthorizationState {
  isCheckingAccess: boolean;
  hasAccess: boolean | null;
  error: Error | null;
  lastChecked: number | null;
}

const CACHE_DURATION = 5 * 60 * 1000;

export const useIsAuthorized = () => {
  const msalDetails = useMsal();
  const navigate = useCustomNavigate();
  const [enableDeveloperLogs] = useFeatureFlag('DEVELOPER_LOGS');
  const abortControllerRef = useRef<AbortController | null>(null);

  const [authState, setAuthState] = useState<AuthorizationState>({
    isCheckingAccess: true,
    hasAccess: null,
    error: null,
    lastChecked: null,
  });

  const navigateToUnauthorized = useCallback(
    (errorType?: string) => {
      navigate({
        to: '/unauthorized',
        keptQueryParams: [FEATURE_FLAG_QUERY_PARAM],
        searchParam: errorType ? `errorType=${errorType}` : undefined,
      });
    },
    [navigate],
  );

  const checkAccess = useCallback(async () => {
    const now = Date.now();

    if (authState.lastChecked && now - authState.lastChecked < CACHE_DURATION && authState.hasAccess !== null) {
      setAuthState((prev) => ({ ...prev, isCheckingAccess: false }));
      return;
    }

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    setAuthState((prev) => ({ ...prev, isCheckingAccess: true, error: null }));

    try {
      const token = await getAuthToken(msalDetails, navigate, enableDeveloperLogs);
      if (!token) {
        setAuthState({
          isCheckingAccess: false,
          hasAccess: false,
          error: new Error('No authentication token'),
          lastChecked: now,
        });
        return;
      }

      const response = await fetch(`${ABWP_API_URL}/users/userinfo`, {
        headers: { Authorization: token, 'Content-Type': 'application/json' },
        signal,
        cache: 'no-cache',
      });

      if (signal.aborted) return;

      if (!response.ok) {
        const { status } = response;
        let errorType: string;

        if (status === 401 || status === 403) {
          errorType = 'authorization';
        } else if (status >= 500) {
          errorType = 'technical';
        } else if (status >= 400) {
          errorType = 'authorization';
        } else {
          errorType = 'technical';
        }

        const error = new Error(`ABWP API error: ${response.status} ${response.statusText}`);
        setAuthState({ isCheckingAccess: false, hasAccess: false, error, lastChecked: now });
        navigateToUnauthorized(errorType);
        return;
      }

      const userData = (await response.json()) as unknown;
      const userHasAccess = hasAccess(userData);

      setAuthState({ isCheckingAccess: false, hasAccess: userHasAccess, error: null, lastChecked: now });

      if (!userHasAccess) {
        navigateToUnauthorized('authorization');
      }
    } catch (error) {
      if (signal.aborted) return;

      // All catch scenarios are considered technical errors (CORS, network, etc.)
      const errorType = 'technical';

      const errorMessage = error instanceof Error ? error : new Error('Unknown error during access check');
      setAuthState({ isCheckingAccess: false, hasAccess: false, error: errorMessage, lastChecked: now });
      navigateToUnauthorized(errorType);
    }
  }, [msalDetails, navigate, enableDeveloperLogs, authState.lastChecked, authState.hasAccess, navigateToUnauthorized]);

  useEffect(() => {
    // eslint-disable-next-line no-void -- We want to ignore the promise result
    void checkAccess();
    return () => abortControllerRef.current?.abort();
  }, [checkAccess]);

  return {
    isCheckingAccess: authState.isCheckingAccess,
    hasAccess: authState.hasAccess,
    error: authState.error,
    recheckAccess: checkAccess,
  };
};

export const useUserData = () => {
  const msalDetails = useMsal();
  const navigate = useCustomNavigate();
  const [enableDeveloperLogs] = useFeatureFlag('DEVELOPER_LOGS');
  const abortControllerRef = useRef<AbortController | null>(null);

  const [userDataState, setUserDataState] = useState<{
    isLoading: boolean;
    userData: AbwpUserData | null;
    error: Error | null;
    lastChecked: number | null;
  }>({
    isLoading: true,
    userData: null,
    error: null,
    lastChecked: null,
  });

  const fetchUserData = useCallback(async () => {
    const now = Date.now();

    setUserDataState((currentState) => {
      if (currentState.lastChecked && now - currentState.lastChecked < CACHE_DURATION && currentState.userData) {
        return currentState;
      }
      return { ...currentState, isLoading: true, error: null };
    });

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    try {
      const token = await getAuthToken(msalDetails, navigate, enableDeveloperLogs);
      if (!token) {
        setUserDataState({
          isLoading: false,
          userData: null,
          error: new Error('No authentication token'),
          lastChecked: now,
        });
        return;
      }

      const response = await fetch(`${ABWP_API_URL}/users/userinfo`, {
        headers: { Authorization: token, 'Content-Type': 'application/json' },
        signal,
        cache: 'no-cache',
      });

      if (signal.aborted) return;

      if (!response.ok) {
        const error = new Error(`Failed to fetch user data: ${response.status} ${response.statusText}`);
        setUserDataState({ isLoading: false, userData: null, error, lastChecked: now });
        return;
      }

      const userData = (await response.json()) as AbwpUserData;
      setUserDataState({ isLoading: false, userData, error: null, lastChecked: now });
    } catch (error) {
      if (signal.aborted) return;

      const errorMessage = error instanceof Error ? error : new Error('Unknown error occurred');
      setUserDataState({ isLoading: false, userData: null, error: errorMessage, lastChecked: now });
    }
  }, [msalDetails, navigate, enableDeveloperLogs]);

  useEffect(() => {
    // eslint-disable-next-line no-void -- We want to ignore the promise result
    void fetchUserData();
    return () => abortControllerRef.current?.abort();
  }, [fetchUserData]);

  const hasDriverAnalysisAccess = useMemo(() => {
    if (!userDataState.userData?.investmentTypes) return false;
    return (
      userDataState.userData.investmentTypes.includes('All') || userDataState.userData.investmentTypes.includes('Media')
    );
  }, [userDataState.userData?.investmentTypes]);

  return {
    isLoading: userDataState.isLoading,
    userData: userDataState.userData,
    error: userDataState.error,
    hasDriverAnalysisAccess,
    refetch: fetchUserData,
  };
};

const httpCall = async <T>(
  config: AxiosRequestConfig,
  msalDetails: IMsalContext,
  navigate: NavigateFunction,
  enableDeveloperLogs: boolean,
  recursiveCall: boolean = false,
): Promise<Response<T>> => {
  const token = await getAuthToken(msalDetails, navigate, enableDeveloperLogs);
  if (!token) return { success: false };

  const headerConfig: AxiosRequestConfig = {
    ...config,
    headers: { ...config.headers, Authorization: token },
    validateStatus: config.validateStatus ?? ((status) => status < 400),
  };

  try {
    const response = await axios<T>(headerConfig);
    return { success: true, data: response.data };
  } catch (err) {
    if (isAxiosError(err)) {
      const status = err.response?.status;
      if (status === 401) {
        sessionStorage.removeItem(AUTH_TOKEN_KEY);
        if (recursiveCall) {
          navigate({ to: '/unauthorized', keptQueryParams: [FEATURE_FLAG_QUERY_PARAM] });
          return { success: false };
        }
        return httpCall(config, msalDetails, navigate, enableDeveloperLogs, true);
      }
      if (status === 403) {
        navigate({ to: '/unauthorized', keptQueryParams: [FEATURE_FLAG_QUERY_PARAM] });
        return { success: false, err: err.response };
      }
      /* To-Do : Remove the below status when we get proper 401 error from API if user don't have access */
      if (status === 500 && (err.response?.data as { success: boolean }).success === false) {
        navigate({ to: '/unauthorized', keptQueryParams: [FEATURE_FLAG_QUERY_PARAM] });
        return { success: false, err: err.response };
      }
    }
    throw err;
  }
};

export type AuthQueryOptions = {
  dependencies?: boolean[];
  notifyOnChangeProps?: NotifyOnChangeProps;
};

export const useAuthenticatedQuery = <T>(
  queryKey: unknown[],
  queryConfig: AxiosRequestConfig,
  additionalOptions?: AuthQueryOptions,
  refetchOnWindowFocus = false,
): UseQueryResult<Response<T>> => {
  const msalDetails = useMsal();
  const navigate = useCustomNavigate();
  const [enableDeveloperLogs] = useFeatureFlag('DEVELOPER_LOGS');

  const result = useQuery({
    queryKey,
    queryFn: () => httpCall<T>(queryConfig, msalDetails, navigate, enableDeveloperLogs),
    enabled: additionalOptions?.dependencies?.every(Boolean) ?? true,
    notifyOnChangeProps: additionalOptions?.notifyOnChangeProps ?? ['data', 'error'],
    refetchOnWindowFocus,
    retry: false,
    throwOnError: false,
    retryOnMount: true,
  });

  useEffect(() => {
    if ((import.meta.env.DEV || enableDeveloperLogs) && result.error) {
      console.error(result.error);
    }
  }, [enableDeveloperLogs, result.error]);

  return result;
};

export const useAuthenticatedMutation = <T, V = unknown>(
  mutationConfig: AxiosRequestConfig,
  options?: UseMutationOptions<Response<T>, Error, V>,
) => {
  const msalDetails = useMsal();
  const navigate = useCustomNavigate();
  const [enableDeveloperLogs] = useFeatureFlag('DEVELOPER_LOGS');

  return useMutation({
    mutationFn: (variables: V) => {
      const config = { ...mutationConfig };
      if (variables && mutationConfig.method !== 'GET') {
        config.data = variables;
      }
      return httpCall<T>(config, msalDetails, navigate, enableDeveloperLogs);
    },
    ...options,
  });
};

type UseAuthenticatedQueries = {
  queryKey: unknown[];
  queryConfig: AxiosRequestConfig;
  additionalOptions?: AuthQueryOptions;
  refetchOnWindowFocus?: boolean;
};

export const useAuthenticatedQueries = <T>(queryList: UseAuthenticatedQueries[]): UseQueryResult<Response<T>>[] => {
  const msalDetails = useMsal();
  const navigate = useCustomNavigate();
  const [enableDeveloperLogs] = useFeatureFlag('DEVELOPER_LOGS');

  const result = useQueries({
    queries: queryList.map(({ queryKey, queryConfig, additionalOptions, refetchOnWindowFocus = false }) => ({
      queryKey,
      queryFn: () => httpCall<T>(queryConfig, msalDetails, navigate, enableDeveloperLogs),
      enabled: additionalOptions?.dependencies?.every(Boolean) ?? true,
      notifyOnChangeProps: additionalOptions?.notifyOnChangeProps ?? ['data', 'error'],
      refetchOnWindowFocus,
      retry: false,
      throwOnError: true,
      retryOnMount: true,
    })),
  });

  const errors = useMemo(() => result.filter(({ error }) => error).map(({ error }) => error), [result]);

  useEffect(() => {
    if ((import.meta.env.DEV || enableDeveloperLogs) && errors.length > 0) {
      console.error(errors);
    }
  }, [enableDeveloperLogs, errors]);

  return result;
};

/** @internal */
export const B2C_URL = (() => import.meta.env.VITE_B2C_KNOWN_AUTHORITY)();
/** @internal */
export const LOGIN_URL = '/users/login';
/** @internal */
export const SCENARIO_URL = '/api/scenarios';
/** @internal */
export const HISTORICAL_URL = '/api/historical-decomp/';
/** @internal */
export const GET_USER_DETAILS_URL = 'api/users/info';
/** @internal */
export const GET_DRIVER_DISCRIPTION_URL = '/api/historical-decomp/mapping/driver-desc-url';
/** @internal */
export const GET_HISTORICAL_BRAND_URL = '/api/historical-decomp/homepage/landing-brands';
/** @internal */
export const GET_DRAFT_DATA = '/api/pnl-draft';
/** @internal */
export const POST_DRAFT_DATA = '/api/pnl-draft';
/** @internal */
export const GET_SCENARIO_DATA = '/api/optimizer/scenario';
/** @internal */
export const GET_PARTITION_DATA = '/api/commercial_priority_mapping';
/** @internal */
export const INVESTMENT_CHART = '/api/historical-decomp/insight/inv-ev-chart';
/** @internal */
export const DR_INVESTMENT_CHART = '/api/historical-decomp/insight/delta-inv-eff-chart';
/** @internal */
export const POST_SAVE_SIMULATION_DATA = '/api/save-simulation';
/** @internal */
export const DELETE_BULK_SIMULATION = '/api/delete-simulations';
/** @internal */
export const GET_DRIVERS_DATA = '/api/historical-decomp/mapping/chart-signals';
/** @internal */
export const GET_COLUMN_CHART_DATA = '/api/historical-decomp/insight/clustered-chart';
/** @internal */
export const GET_TOP_BOTTOM_CHART = '/api/historical-decomp/insight/top-bottom-chart';
/** @internal */
export const GET_STACKED_LINE_CHART = '/api/historical-decomp/insight/stacked-line-chart';
/** @internal */
export const GET_INVEST_EFFEC_CHART_DATA = '/api/historical-decomp/insight/invest-efficiency/tabular/revenue';
/** @internal */
export const POST_SAVE_PL_PROTOTYPE = '/api/optimizer/scenario/pnl-opt:testing5/';
/** @internal */
export const GET_DISTRIBUTION_TABLE_DATA = '/api/historical-decomp/distribution/chart';
/** @internal */
export const POST_LONG_TERM_IMPACT = '/api/historical-decomp/long-term-impact';
/** @internal */
export const POST_MOONLIGHT_WALK_THROUGH = '/api/historical-decomp/moonlight-walk-through';
